import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Info, Loader2, XCircle } from 'lucide-react';
import * as React from 'react';

import { IdentityApiError, type IdentityErrorCode } from '@/api/identity-client';
import { LegalApiError } from '@/api/legal-client';
import { useActivateSupplier } from '@/api/use-identity';
import { useHasSignedCgu } from '@/api/use-legal';
import { useSupplierMeClient } from '@/api/supplier-me-client-context';
import type { SupplierMeDto } from '@/api/supplier-me-client';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

// =============================================================================
// activation-screen.tsx — Écran « Activation initiale » (S0-ACTIVATION, F1/F2/F3/F4).
// =============================================================================
// NOUVEAU parcours d'activation (route `/onboarding/activate/$token`), DISTINCT du
// magic-link (reconnexion, `/onboarding/$token`). À l'arrivée avec un token, l'UI :
//   1. identity.activate(token) — consomme l'ActivationToken (usage unique strict) ;
//      l'API active le credential ET ouvre la SupplierSession (pose les cookies). F2.
//   2. GET /supplier/contacts/me (session ouverte) — lit les coordonnées à confirmer
//      (nom / prénom / email, F3). AFFICHAGE SEUL : pas de formulaire éditable (F4,
//      DBU:2359 « confirmation », pas « correction »).
//   3. legal.hasSigned() — sondé après ouverture de session, pilote la suite (EF-IDA-4).
//   4. CTA « Confirmer et continuer » → l'appelant re-résout l'identité (role:'supplier')
//      puis route : CGU (F-02) si non signées, grille sinon.
// En échec d'activation (token invalide/expiré/consommé), message clair + PAS de CTA
// (pas de crash). Composant PRÉSENTATIONNEL : reçoit `token` + `onConfirmed` (testable
// hors routeur) ; l'adaptateur de route lit le token d'URL et déclenche la navigation.
//
// a11y (WCAG 2.1 AA) : <h1> unique, statut de chargement en role="status", erreur en
// role="alert", coordonnées en liste de description (<dl>), icônes décoratives aria-hidden.

/** Issue de la confirmation : statut CGU, pour router la suite (EF-IDA-4). */
export interface ActivationOutcome {
  /** CGU courantes signées ? (legal.hasSigned) — pilote la redirection (grille vs F-02). */
  readonly hasSigned: boolean;
}

export interface ActivationScreenProps {
  /** Token d'activation extrait de l'URL (param de route). */
  readonly token: string;
  /**
   * Appelé au clic sur « Confirmer et continuer » (session ouverte, coordonnées confirmées) :
   * l'appelant re-résout l'identité fournisseur puis route vers la suite (CGU si `!hasSigned`).
   */
  readonly onConfirmed: (outcome: ActivationOutcome) => void;
}

/** Message de remédiation selon le code d'erreur d'activation (E1/E2). */
function activationErrorHint(code: IdentityErrorCode): string {
  switch (code) {
    case 'TOKEN_CONSUMED':
      return 'Ce lien a déjà été utilisé. Reconnectez-vous depuis la page de connexion.';
    case 'TOKEN_EXPIRED':
      return "Ce lien d'activation a expiré. Contactez votre administrateur pour en obtenir un nouveau.";
    case 'TOKEN_INVALID':
    case 'RATE_LIMITED':
    case 'UNKNOWN':
      return "Ce lien d'activation n'est pas valide. Contactez votre administrateur.";
  }
}

export function ActivationScreen({
  token,
  onConfirmed,
}: ActivationScreenProps): React.JSX.Element {
  const meClient = useSupplierMeClient();
  const activate = useActivateSupplier();
  // Coordonnées à confirmer + statut CGU sondés UNIQUEMENT après ouverture de session
  // (activation réussie) : gate `enabled` — sinon me/has-signed partiraient sans session (401).
  const me = useQuery<SupplierMeDto, Error>({
    queryKey: ['supplier-me', 'activation'],
    queryFn: () => meClient.getMe(),
    enabled: activate.isSuccess,
  });
  const cgu = useHasSignedCgu({ enabled: activate.isSuccess });

  // Activation une seule fois à l'arrivée (usage unique strict). Le ref évite une double
  // consommation sous StrictMode / re-render (le 2e appel échouerait TOKEN_CONSUMED côté serveur).
  const triggered = React.useRef(false);
  React.useEffect(() => {
    if (triggered.current || token.length === 0) {
      return;
    }
    triggered.current = true;
    activate.mutate(token);
    // activate stable pour la durée de vie de l'écran ; déclenché une seule fois (garde `triggered`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const activationError =
    activate.error instanceof IdentityApiError ? activate.error : undefined;
  const activationErrorCode: IdentityErrorCode =
    activationError?.code ?? 'UNKNOWN';

  const handleConfirm = React.useCallback((): void => {
    if (cgu.data === undefined) {
      return;
    }
    onConfirmed({ hasSigned: cgu.data.hasSigned });
  }, [cgu.data, onConfirmed]);

  // DÉGRADÉ GRACIEUX (bug prod onboarding) : activation OK (session ouverte) MAIS aucune CGU
  // publiée côté admin → has-signed renvoie 404 (NoPublishedCguError). Ce n'est PAS une panne
  // ni une faute du fournisseur : le compte EST activé, il n'y a simplement rien à signer tant
  // que l'admin n'a pas publié de texte. Message INFORMATIF (role=status), sans crash, SANS CTA
  // de confirmation (on ne route ni vers la grille ni vers la signature : rien à signer).
  const noCguPublished =
    activate.isSuccess &&
    cgu.error instanceof LegalApiError &&
    cgu.error.isNoPublishedCgu;

  const isActivating = activate.isPending || (!activate.isSuccess && !activate.isError);
  const isReady = activate.isSuccess && me.isSuccess && cgu.isSuccess;
  const isLoadingDetails =
    activate.isSuccess && !noCguPublished && (me.isLoading || cgu.isLoading);
  // Le 404 « aucune CGU publiée » est traité à part (message informatif), pas en erreur technique.
  const detailsError =
    activate.isSuccess && (me.isError || (cgu.isError && !noCguPublished));

  return (
    <div className="mx-auto max-w-md space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Activation de votre compte
        </h1>
      </header>

      <Card aria-labelledby="activation-title">
        <CardHeader>
          <CardTitle id="activation-title" className="text-lg">
            Confirmez vos coordonnées
          </CardTitle>
          <CardDescription>
            Vérifiez les informations ci-dessous, puis confirmez pour accéder à
            votre espace fournisseur.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isActivating ? (
            <div
              role="status"
              className="flex items-center gap-3 text-muted-foreground"
            >
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              <span>Activation de votre compte…</span>
            </div>
          ) : null}

          {activate.isError ? (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-4"
            >
              <XCircle
                className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
                aria-hidden="true"
              />
              <div className="space-y-1">
                <p className="font-medium text-destructive">
                  Activation impossible
                </p>
                <p className="text-sm text-foreground">
                  {activationErrorHint(activationErrorCode)}
                </p>
              </div>
            </div>
          ) : null}

          {isLoadingDetails ? (
            <div
              role="status"
              className="flex items-center gap-3 text-muted-foreground"
            >
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              <span>Chargement de vos coordonnées…</span>
            </div>
          ) : null}

          {detailsError ? (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-4"
            >
              <XCircle
                className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
                aria-hidden="true"
              />
              <div className="space-y-1">
                <p className="font-medium text-destructive">
                  Coordonnées indisponibles
                </p>
                <p className="text-sm text-foreground">
                  Votre compte est activé, mais vos coordonnées n'ont pas pu être
                  chargées. Rechargez la page ou réessayez plus tard.
                </p>
              </div>
            </div>
          ) : null}

          {noCguPublished ? (
            // Dégradé gracieux : compte activé mais aucune CGU publiée (404). Message informatif
            // (role=status), couleur DOUBLÉE d'un libellé explicite, pas de crash, PAS de CTA de
            // confirmation (rien à signer). L'icône est décorative — le texte porte le sens.
            <div
              role="status"
              className="flex items-start gap-3 rounded-md border border-border bg-muted p-4"
            >
              <Info
                className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <div className="space-y-1">
                <p className="font-medium text-foreground">
                  Votre connexion a réussi
                </p>
                <p className="text-sm text-foreground">
                  Les conditions générales ne sont pas encore disponibles ;
                  contactez l&apos;administrateur JeKo pour finaliser votre accès.
                </p>
              </div>
            </div>
          ) : null}

          {isReady && me.data !== undefined ? (
            <>
              <div
                role="status"
                className="flex items-start gap-3 rounded-md border border-success/40 bg-success/10 p-4"
              >
                <CheckCircle2
                  className="mt-0.5 h-5 w-5 shrink-0 text-success"
                  aria-hidden="true"
                />
                <p className="text-sm font-medium text-success">
                  Votre compte est activé.
                </p>
              </div>

              <dl className="grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-4 gap-y-2 text-sm">
                <dt className="font-medium text-muted-foreground">Nom</dt>
                <dd className="text-foreground">{me.data.nom}</dd>
                <dt className="font-medium text-muted-foreground">Prénom</dt>
                <dd className="text-foreground">{me.data.prenom}</dd>
                <dt className="font-medium text-muted-foreground">E-mail</dt>
                <dd className="break-all text-foreground">{me.data.email}</dd>
              </dl>

              <Button type="button" className="w-full" onClick={handleConfirm}>
                Confirmer et continuer
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
